/**
 * The invoice editor's state and the pure functions around it, kept out of
 * the view so they can be unit-tested: what the form holds (text, exactly as
 * typed), what is sent to the API (integer minor units, PERCENT in basis
 * points), the live preview (invoiceMath with the shop's tax rates) and the
 * checks the server makes on issue, run first so problems show on the fields.
 */
import type { CustomerSnapshot, DocumentInput, InvoiceDoc, InvoiceSettings, LineInput } from '../../internals/repository/InvoiceRepository';
import type { PreviewDoc } from './components/InvoicePreview';
import { addDays, parseMoney, moneyInput, termDays, todayColombo } from './format';
import { computeTotals, MathDiscount } from './math/invoiceMath';

export type LineKind = 'PRODUCT' | 'SERVICE' | 'COURSE' | 'CUSTOM';
export type DiscountKind = 'AMOUNT' | 'PERCENT';

export interface EditorLine {
  /** Stable React key and drag identity; equals lineId once the server has one. */
  key: string;
  lineId?: string;
  kind: LineKind;
  refId?: string;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  discount: string;
  discountType: DiscountKind;
  /** '' = no tax. */
  taxCode: string;
}

export interface EditorCharge { key: string; label: string; amount: string }

/**
 * `client`: picked from the shop's customers, sent as `{ clientId }` so the
 * server snapshots the record. `snapshot`: typed inline or loaded from a
 * saved document, sent as given.
 */
export interface EditorCustomer {
  source: 'client' | 'snapshot';
  clientId: string | null;
  name: string;
  companyName: string;
  phone: string;
  email: string;
  address: string;
  tin: string;
  vatRegistered: boolean;
}

export interface EditorState {
  docType: 'INVOICE' | 'QUOTATION';
  isTaxInvoice: boolean;
  customer: EditorCustomer | null;
  issueDate: string;
  supplyDate: string;
  paymentTerms: string;
  customTermDays: string;
  validUntil: string;
  lines: EditorLine[];
  invoiceDiscount: string;
  invoiceDiscountType: DiscountKind;
  charges: EditorCharge[];
  deposit: string;
  notes: string;
  terms: string;
  revisionNote: string;
}

export interface Problems { banner: string[]; fields: Record<string, string> }

let seq = 0;
export const newKey = (p = 'k') => `${p}${Date.now().toString(36)}${(seq++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** New lines carry the shop's default tax only when the shop charges VAT. */
export const lineTaxDefault = (settings?: InvoiceSettings | null, isTaxInvoice = false) =>
  settings?.taxInvoiceEnabled || isTaxInvoice
    ? (settings?.taxRates?.find((r) => r.isDefault) ?? settings?.taxRates?.[0])?.code ?? ''
    : '';

export function blankLine(taxCode = '', patch: Partial<EditorLine> = {}): EditorLine {
  return {
    key: newKey('l'), kind: 'CUSTOM', description: '', quantity: '1', unit: '', unitPrice: '',
    discount: '', discountType: 'AMOUNT', taxCode, ...patch,
  };
}

export function blankState(settings: InvoiceSettings | null | undefined, docType: 'INVOICE' | 'QUOTATION' = 'INVOICE', today = todayColombo()): EditorState {
  return {
    docType,
    isTaxInvoice: false,
    customer: null,
    issueDate: today,
    supplyDate: today,
    paymentTerms: settings?.defaultPaymentTerms || 'NET_15',
    customTermDays: String(settings?.defaultCustomTermDays ?? 20),
    validUntil: addDays(today, settings?.quotationValidDays ?? 14),
    lines: [],
    invoiceDiscount: '',
    invoiceDiscountType: 'AMOUNT',
    charges: [],
    deposit: '',
    notes: settings?.defaultNotes ?? '',
    terms: settings?.defaultTerms ?? '',
    revisionNote: '',
  };
}

// ── Parsing ─────────────────────────────────────────────────────────────────

/** "12.5" → 1250 basis points; NaN when not a number with ≤2 decimals. */
export function parsePercentBp(text: string): number {
  const clean = String(text ?? '').replace(/[\s%]/g, '');
  if (!/^\d*(\.\d{0,2})?$/.test(clean) || clean === '' || clean === '.') return NaN;
  const [whole, frac = ''] = clean.split('.');
  return Number(whole || '0') * 100 + Number(frac.padEnd(2, '0'));
}

/** Basis points → editable text: 1250 → "12.5". */
export const percentInput = (bp: number) => {
  const s = `${Math.floor(bp / 100)}.${String(bp % 100).padStart(2, '0')}`;
  return s.replace(/\.?0+$/, '');
};

/** Up to 3 decimals, ≥ 0; NaN otherwise. */
export function parseQty(text: string): number {
  const clean = String(text ?? '').replace(/[,\s]/g, '');
  if (!/^\d*(\.\d{0,3})?$/.test(clean) || clean === '' || clean === '.') return NaN;
  return Number(clean);
}

/**
 * A catalogue price (rupees, stored as a number or a string, sometimes with
 * float noise like 349.99999999) → minor units without float accumulation.
 */
export function priceToMinor(price: unknown): number {
  if (price === null || price === undefined || price === '') return 0;
  const exact = parseMoney(String(price));
  if (!Number.isNaN(exact)) return exact;
  const n = Number(price);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 0;
}

const discountOf = (value: string, type: DiscountKind): MathDiscount | undefined => {
  if (!String(value ?? '').trim()) return undefined;
  const v = type === 'PERCENT' ? parsePercentBp(value) : parseMoney(value);
  return Number.isNaN(v) || v <= 0 ? undefined : { type, value: v };
};

const money0 = (text: string) => {
  const v = parseMoney(text);
  return Number.isNaN(v) ? 0 : v;
};

/** A line the user touched; untouched blank rows are not sent. */
export const isMeaningful = (l: EditorLine) => !!(l.description.trim() || l.unitPrice.trim() || l.refId);

export const sentLines = (state: EditorState) => state.lines.filter(isMeaningful);

const anyLineTaxed = (state: EditorState) => state.lines.some((l) => isMeaningful(l) && !!l.taxCode);

// ── To and from the API ─────────────────────────────────────────────────────

export function customerInput(c: EditorCustomer | null): DocumentInput['customer'] | undefined {
  if (!c) return undefined;
  // A picked client goes by id so the server snapshots the record. A Buyer TIN
  // typed here is not on the client record yet, so then the snapshot is sent.
  if (c.source === 'client' && c.clientId && !c.tin.trim()) return { clientId: c.clientId };
  const t = (s: string) => s.trim() || undefined;
  const snap: CustomerSnapshot = {
    clientId: c.clientId || null,
    name: c.name.trim(),
    companyName: t(c.companyName),
    phoneE164: t(c.phone),
    email: t(c.email),
    address: t(c.address),
    tin: t(c.tin),
    vatRegistered: c.vatRegistered,
  };
  return snap;
}

export function toDocumentInput(state: EditorState): DocumentInput {
  const isQuote = state.docType === 'QUOTATION';
  const taxedCharges = anyLineTaxed(state);
  const lines: LineInput[] = sentLines(state).map((l) => {
    const q = parseQty(l.quantity);
    const price = parseMoney(l.unitPrice);
    const out: LineInput = {
      kind: l.kind,
      description: l.description.trim(),
      quantity: Number.isNaN(q) ? 0 : q,
      unitPriceMinor: Number.isNaN(price) ? 0 : price,
    };
    if (l.lineId) out.lineId = l.lineId;
    if (l.refId) out.refId = l.refId;
    if (l.unit.trim()) out.unit = l.unit.trim();
    const d = discountOf(l.discount, l.discountType);
    if (d) out.discount = d;
    if (l.taxCode) out.taxCode = l.taxCode;
    return out;
  });
  const input: DocumentInput = {
    docType: state.docType,
    customer: customerInput(state.customer),
    issueDate: state.issueDate,
    isTaxInvoice: !isQuote && state.isTaxInvoice,
    lines,
    charges: state.charges
      .filter((c) => c.label.trim() || c.amount.trim())
      .map((c) => ({ label: c.label.trim(), amountMinor: money0(c.amount), taxable: taxedCharges })),
    notes: state.notes,
    terms: state.terms,
  };
  const inv = discountOf(state.invoiceDiscount, state.invoiceDiscountType);
  if (inv) input.invoiceDiscount = inv;
  if (isQuote) {
    if (state.validUntil) input.validUntil = state.validUntil;
  } else {
    input.paymentTerms = state.paymentTerms as DocumentInput['paymentTerms'];
    if (state.paymentTerms === 'CUSTOM') {
      const days = Number(state.customTermDays);
      input.customTermDays = Number.isInteger(days) ? days : 0;
    }
    input.depositAppliedMinor = money0(state.deposit);
    if (state.isTaxInvoice && state.supplyDate) input.supplyDate = state.supplyDate;
  }
  if (!input.customer) delete input.customer;
  return input;
}

export function fromDocument(doc: InvoiceDoc): EditorState {
  const c = doc.customer;
  return {
    docType: doc.docType === 'QUOTATION' ? 'QUOTATION' : 'INVOICE',
    isTaxInvoice: !!doc.isTaxInvoice,
    customer: c ? {
      source: 'snapshot',
      clientId: c.clientId ?? null,
      name: c.name ?? '',
      companyName: c.companyName ?? '',
      phone: c.phoneE164 ?? '',
      email: c.email ?? '',
      address: c.address ?? '',
      tin: c.tin ?? '',
      vatRegistered: !!c.vatRegistered,
    } : null,
    issueDate: doc.issueDate,
    supplyDate: doc.supplyDate || doc.issueDate,
    paymentTerms: doc.paymentTerms || 'NET_15',
    customTermDays: String(doc.customTermDays ?? 20),
    validUntil: doc.validUntil || '',
    lines: (doc.lines || []).map((l) => ({
      key: l.lineId || newKey('l'),
      lineId: l.lineId,
      kind: l.kind,
      refId: l.refId,
      description: l.description,
      quantity: String(l.quantity),
      unit: l.unit ?? '',
      unitPrice: moneyInput(l.unitPriceMinor),
      discount: l.discount?.value ? (l.discount.type === 'PERCENT' ? percentInput(l.discount.value) : moneyInput(l.discount.value)) : '',
      discountType: l.discount?.type ?? 'AMOUNT',
      taxCode: l.taxCode ?? '',
    })),
    invoiceDiscount: doc.invoiceDiscount?.value
      ? (doc.invoiceDiscount.type === 'PERCENT' ? percentInput(doc.invoiceDiscount.value) : moneyInput(doc.invoiceDiscount.value))
      : '',
    invoiceDiscountType: doc.invoiceDiscount?.type ?? 'AMOUNT',
    charges: (doc.charges || []).map((ch) => ({ key: newKey('c'), label: ch.label, amount: moneyInput(ch.amountMinor) })),
    deposit: doc.depositAppliedMinor ? moneyInput(doc.depositAppliedMinor) : '',
    notes: doc.notes ?? '',
    terms: doc.terms ?? '',
    revisionNote: '',
  };
}

/** After a save, give the sent lines the server's lineIds (same order). */
export function withSavedLineIds(state: EditorState, doc: InvoiceDoc): EditorState {
  const sent = sentLines(state);
  const ids = new Map(sent.map((l, i) => [l.key, doc.lines?.[i]?.lineId]));
  return { ...state, lines: state.lines.map((l) => (ids.get(l.key) ? { ...l, lineId: ids.get(l.key) } : l)) };
}

/** Can this document be opened in the editor, and how? */
export function editMode(doc: InvoiceDoc): 'draft' | 'revision' | 'locked' {
  if (doc.status === 'DRAFT' && (doc.docType === 'INVOICE' || doc.docType === 'QUOTATION')) return 'draft';
  const money = (doc.paidMinor || 0) + (doc.creditedMinor || 0) + (doc.depositAppliedMinor || 0) + (doc.pendingClaimMinor || 0);
  if (doc.docType === 'INVOICE' && doc.status === 'ISSUED' && !doc.isTaxInvoice && money === 0) return 'revision';
  return 'locked';
}

// ── Preview ─────────────────────────────────────────────────────────────────

export const dueDateOf = (state: EditorState) =>
  addDays(state.issueDate, termDays(state.paymentTerms, Number(state.customTermDays) || 0));

export interface EditorPreview extends PreviewDoc {
  lineDiscountMinor: number;
  invoiceDiscountMinor: number;
}

export function previewDoc(
  state: EditorState,
  settings: InvoiceSettings | null | undefined,
  shop?: { currency?: string } | null,
): EditorPreview {
  const rates = new Map((settings?.taxRates || []).map((r) => [r.code, r]));
  const def = (settings?.taxRates || []).find((r) => r.isDefault) || settings?.taxRates?.[0];
  const isQuote = state.docType === 'QUOTATION';
  const isTax = !isQuote && state.isTaxInvoice;
  const lines = state.lines.map((l) => {
    const q = parseQty(l.quantity);
    const p = parseMoney(l.unitPrice);
    return {
      key: l.key,
      description: l.description,
      quantity: Number.isNaN(q) ? 0 : q,
      unit: l.unit,
      unitPriceMinor: Number.isNaN(p) ? 0 : p,
      discount: discountOf(l.discount, l.discountType),
      taxRateBp: l.taxCode ? rates.get(l.taxCode)?.rateBp ?? 0 : 0,
    };
  });
  const taxed = anyLineTaxed(state);
  const charges = state.charges
    .filter((c) => c.label.trim() || c.amount.trim())
    .map((c) => ({ label: c.label || 'Charge', amountMinor: money0(c.amount), taxable: taxed }));
  const deposit = isQuote ? 0 : money0(state.deposit);
  const r = computeTotals({
    lines,
    invoiceDiscount: discountOf(state.invoiceDiscount, state.invoiceDiscountType),
    charges,
    defaultTaxRateBp: def?.rateBp || 0,
    roundToWholeUnit: isTax && settings?.roundTaxInvoiceToWholeRupee === true,
    depositAppliedMinor: deposit,
  });
  const lineDiscountMinor = r.lineDiscountsMinor.reduce((a, b) => a + b, 0);
  const label = (rateBp: number) =>
    (def?.rateBp === rateBp ? def : (settings?.taxRates || []).find((x) => x.rateBp === rateBp))?.label || `Tax ${rateBp / 100}%`;
  const c = state.customer;
  return {
    docType: state.docType,
    number: null,
    status: 'DRAFT',
    isTaxInvoice: isTax,
    customer: c ? { name: c.name, companyName: c.companyName, address: c.address, tin: c.tin, phoneE164: c.phone, email: c.email } : null,
    issueDate: state.issueDate,
    supplyDate: isTax ? state.supplyDate : undefined,
    dueDate: isQuote ? undefined : dueDateOf(state),
    validUntil: isQuote ? state.validUntil : undefined,
    paymentTerms: state.paymentTerms,
    customTermDays: Number(state.customTermDays) || 0,
    lines: lines.map((l, k) => ({
      lineId: l.key, description: l.description, quantity: l.quantity, unit: l.unit,
      unitPriceMinor: l.unitPriceMinor, discountMinor: r.lineDiscountsMinor[k],
      lineTotalMinor: r.lineTotalsMinor[k], taxRateBp: l.taxRateBp,
    })),
    charges: charges.map(({ label: l, amountMinor }) => ({ label: l, amountMinor })),
    subtotalMinor: r.subtotalMinor,
    discountMinor: r.discountMinor,
    chargesMinor: r.chargesMinor,
    taxLines: r.taxLines.map((t) => ({ ...t, label: label(t.rateBp) })),
    roundingMinor: r.roundingMinor,
    totalMinor: r.totalMinor,
    depositAppliedMinor: deposit,
    paidMinor: 0,
    creditedMinor: 0,
    balanceMinor: r.balanceMinor,
    amountInWords: r.amountInWords,
    notes: state.notes,
    terms: state.terms,
    currency: shop?.currency || 'LKR',
    lineDiscountMinor,
    invoiceDiscountMinor: r.discountMinor - lineDiscountMinor,
  };
}

/** 10% of the subtotal after line and invoice discounts, in minor units. */
export function serviceChargeMinor(state: EditorState, settings?: InvoiceSettings | null): number {
  const p = previewDoc(state, settings);
  const base = p.subtotalMinor - p.discountMinor;
  return Math.max(0, Math.round(base / 10));
}

// ── Validation ──────────────────────────────────────────────────────────────

/** Editor field keys: `customer`, `customer.name`, `buyerTin`, `supplyDate`,
 *  `issueDate`, `paymentTerms`, `customTermDays`, `validUntil`, `lines`,
 *  `line.<key>.<field>`, `charge.<key>.<field>`, `invoiceDiscount`, `deposit`,
 *  `notes`, `terms`, `revisionNote`. */
export const lineField = (key: string, f: string) => `line.${key}.${f}`;
export const chargeField = (key: string, f: string) => `charge.${key}.${f}`;

/** Values the server would refuse as malformed, checked on every save. */
export function validateForSave(state: EditorState): Problems {
  const fields: Record<string, string> = {};
  const banner: string[] = [];
  const add = (key: string, msg: string) => { fields[key] = msg; banner.push(msg); };
  if (!state.customer) add('customer', 'Choose a customer.');
  else if (!state.customer.clientId && !state.customer.name.trim()) add('customer.name', 'The customer needs a name.');
  sentLines(state).forEach((l) => {
    const n = state.lines.indexOf(l) + 1;
    if (l.quantity.trim() && Number.isNaN(parseQty(l.quantity))) add(lineField(l.key, 'quantity'), `Line ${n}: quantity must be a number with up to 3 decimals.`);
    if (l.unitPrice.trim() && Number.isNaN(parseMoney(l.unitPrice))) add(lineField(l.key, 'unitPrice'), `Line ${n}: unit price is not a valid amount.`);
    if (l.discount.trim()) {
      const v = l.discountType === 'PERCENT' ? parsePercentBp(l.discount) : parseMoney(l.discount);
      if (Number.isNaN(v) || (l.discountType === 'PERCENT' && v > 10000)) add(lineField(l.key, 'discount'), `Line ${n}: discount is not valid.`);
    }
  });
  if (state.invoiceDiscount.trim()) {
    const v = state.invoiceDiscountType === 'PERCENT' ? parsePercentBp(state.invoiceDiscount) : parseMoney(state.invoiceDiscount);
    if (Number.isNaN(v) || (state.invoiceDiscountType === 'PERCENT' && v > 10000)) add('invoiceDiscount', 'The invoice discount is not valid.');
  }
  if (state.deposit.trim() && Number.isNaN(parseMoney(state.deposit))) add('deposit', 'The deposit is not a valid amount.');
  state.charges.forEach((c) => {
    if (!c.label.trim() && !c.amount.trim()) return;
    if (!c.label.trim()) add(chargeField(c.key, 'label'), 'Each charge needs a name.');
    if (c.amount.trim() && Number.isNaN(parseMoney(c.amount))) add(chargeField(c.key, 'amount'), `${c.label || 'A charge'} is not a valid amount.`);
  });
  if (state.paymentTerms === 'CUSTOM' && state.docType === 'INVOICE') {
    const d = Number(state.customTermDays);
    if (!Number.isInteger(d) || d < 0 || d > 365) add('customTermDays', 'Custom terms must be 0 to 365 days.');
  }
  return { banner, fields };
}

/** Everything the server checks on issue (CONTRACT §4.1, issueProblems). */
export function validateForIssue(state: EditorState, settings: InvoiceSettings | null | undefined): Problems {
  const { banner, fields } = validateForSave(state);
  const add = (key: string, msg: string) => {
    if (!fields[key]) fields[key] = msg;
    banner.push(msg);
  };
  const lines = sentLines(state);
  if (!lines.length) add('lines', 'Add at least one item.');
  lines.forEach((l) => {
    const n = state.lines.indexOf(l) + 1;
    if (!l.description.trim()) add(lineField(l.key, 'description'), `Line ${n} needs a description.`);
    const q = parseQty(l.quantity);
    if (!l.quantity.trim() || !(q > 0)) {
      if (!fields[lineField(l.key, 'quantity')]) add(lineField(l.key, 'quantity'), `Line ${n} needs a quantity.`);
    }
    if (!l.unitPrice.trim()) add(lineField(l.key, 'unitPrice'), `Line ${n} needs a unit price.`);
  });
  if (state.docType === 'QUOTATION') {
    if (state.validUntil && state.validUntil < state.issueDate) add('validUntil', 'Valid until cannot be before the issue date.');
  } else {
    if (dueDateOf(state) < state.issueDate) add('paymentTerms', 'The due date cannot be before the issue date.');
    if (state.isTaxInvoice) {
      if (!settings?.shopTin) add('shopTin', 'Add your shop TIN in Settings → Invoicing to issue a tax invoice.');
      if (!state.supplyDate) add('supplyDate', 'Date of supply is required for a tax invoice.');
      if (state.customer?.vatRegistered && !state.customer.tin.trim()) add('buyerTin', 'Buyer TIN is required for a VAT-registered customer.');
    }
  }
  return { banner, fields };
}

// ── Server errors → fields ──────────────────────────────────────────────────

const LINE_FIELD: Record<string, string> = {
  description: 'description', quantity: 'quantity', unitPriceMinor: 'unitPrice', unit: 'unit',
  discount: 'discount', taxCode: 'taxCode', kind: 'description',
};
const TOP_FIELD: Record<string, string> = {
  customer: 'customer', 'customer.name': 'customer.name', 'customer.clientId': 'customer',
  'customer.tin': 'buyerTin', supplyDate: 'supplyDate', issueDate: 'issueDate', dueDate: 'paymentTerms',
  paymentTerms: 'paymentTerms', customTermDays: 'customTermDays', validUntil: 'validUntil', lines: 'lines',
  invoiceDiscount: 'invoiceDiscount', depositAppliedMinor: 'deposit', notes: 'notes', terms: 'terms',
  revisionNote: 'revisionNote', shopTin: 'shopTin', isTaxInvoice: 'isTaxInvoice', charges: 'charges',
};

const CODE_TEXT: Record<string, string> = {
  required: 'Required.',
  invalid: 'Not valid.',
  too_long: 'Too long.',
  unknown: 'This tax rate no longer exists. Choose another.',
  not_found: 'This customer no longer exists.',
  before_issue_date: 'Cannot be before the issue date.',
  cannot_change_after_issue: 'Cannot be changed after issue.',
};

const FIELD_TEXT: Record<string, string> = {
  shopTin: 'Add your shop TIN in Settings → Invoicing to issue a tax invoice.',
  supplyDate: 'Date of supply is required for a tax invoice.',
  buyerTin: 'Buyer TIN is required for a VAT-registered customer.',
  'customer.name': 'The customer needs a name.',
  customer: 'Choose a customer.',
  lines: 'Add at least one item.',
  paymentTerms: 'The due date cannot be before the issue date.',
  validUntil: 'Valid until cannot be before the issue date.',
};

const FIELD_LABEL: Record<string, string> = {
  customer: 'Customer', 'customer.name': 'Customer name', buyerTin: 'Buyer TIN', supplyDate: 'Date of supply',
  issueDate: 'Issue date', paymentTerms: 'Payment terms', customTermDays: 'Custom terms', validUntil: 'Valid until',
  lines: 'Items', invoiceDiscount: 'Invoice discount', deposit: 'Deposit received', notes: 'Notes', terms: 'Terms',
  revisionNote: 'Reason for the change', shopTin: 'Shop TIN', isTaxInvoice: 'Tax invoice', charges: 'Charges',
};

/** InvoiceApiError.fields (server paths) → editor field keys and messages. */
export function mapServerFields(serverFields: Record<string, string> | null | undefined, state: EditorState): Problems {
  const fields: Record<string, string> = {};
  const banner: string[] = [];
  const sent = sentLines(state);
  const charges = state.charges.filter((c) => c.label.trim() || c.amount.trim());
  Object.entries(serverFields || {}).forEach(([path, code]) => {
    const text = CODE_TEXT[code] || 'Not valid.';
    let m = /^lines\[(\d+)\]\.(\w+)$/.exec(path);
    if (m) {
      const line = sent[Number(m[1])];
      const n = line ? state.lines.indexOf(line) + 1 : Number(m[1]) + 1;
      const f = LINE_FIELD[m[2]] || 'description';
      const msg = `Line ${n}: ${f === 'unitPrice' ? 'unit price' : f === 'taxCode' ? 'tax' : f} — ${text.toLowerCase()}`;
      if (line) fields[lineField(line.key, f)] = msg;
      banner.push(msg);
      return;
    }
    m = /^charges\[(\d+)\]\.(\w+)$/.exec(path);
    if (m) {
      const ch = charges[Number(m[1])];
      const f = m[2] === 'amountMinor' ? 'amount' : 'label';
      const msg = `${ch?.label || 'Charge'}: ${f} — ${text.toLowerCase()}`;
      if (ch) fields[chargeField(ch.key, f)] = msg;
      banner.push(msg);
      return;
    }
    const key = TOP_FIELD[path] || path;
    const msg = (code === 'required' || code === 'before_issue_date') && FIELD_TEXT[key]
      ? FIELD_TEXT[key]
      : `${FIELD_LABEL[key] || key}: ${text.toLowerCase()}`;
    fields[key] = msg;
    banner.push(msg);
  });
  return { banner, fields };
}
